import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import './AuthCard.css';

// Login popup for the landing page, styled to match public/playbook.html.
//
// It lives in React (not inside playbook.html) because authentication state
// belongs to AuthContext: logging in has to update the app, not just the
// iframe. playbook.html's login button postMessages the parent to open this;
// see HomePage.js. The /login route redirects here (to /?login=1), so this
// popup is the only login UI — the old standalone page is never shown.
interface LoginModalProps {
  lang: 'sv' | 'en';
  onClose: () => void;
}

const COPY = {
  sv: {
    eyebrow: 'Hälsosam Urbanism',
    title: 'Logga in',
    lede: 'Analysverktyg för att planera och utvärdera hälsofrämjande stadsmiljöer.',
    username: 'Användarnamn',
    password: 'Lösenord',
    submit: 'Logga in',
    loading: 'Loggar in…',
    forgot: 'Glömt lösenord?',
    close: 'Stäng',
    expired: 'Din inloggning har gått ut. Logga in igen för att fortsätta.',
  },
  en: {
    eyebrow: 'Healthy Urbanism',
    title: 'Log in',
    lede: 'Analysis tools for planning and evaluating health-promoting urban environments.',
    username: 'Username',
    password: 'Password',
    submit: 'Log in',
    loading: 'Logging in…',
    forgot: 'Forgot your password?',
    close: 'Close',
    expired: 'Your session has expired. Log in again to continue.',
  },
};

const LoginModal: React.FC<LoginModalProps> = ({ lang, onClose }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const usernameRef = useRef<HTMLInputElement>(null);

  const { login, error, sessionExpired } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // ProtectedRoute passes the page the user was sent away from (e.g. a tool
  // whose session ran out); go back there after login.
  const from = (location.state as any)?.from;
  const t = COPY[lang] ?? COPY.sv;

  useEffect(() => {
    usernameRef.current?.focus();
  }, []);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    const ok = await login(username, password);
    setIsLoading(false);
    if (ok) navigate(from?.pathname ? `${from.pathname}${from.search || ''}` : '/dashboard');
  };

  return (
    <div
      className="auth-overlay"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="login-title"
    >
      {/* stop clicks inside the card from closing via the overlay handler */}
      <div className="auth-card" onClick={(e) => e.stopPropagation()}>
        <button className="auth-close" onClick={onClose} aria-label={t.close}>×</button>

        <svg className="auth-mark" viewBox="0 0 34 28" aria-hidden="true">
          <ellipse cx="12" cy="14" rx="10" ry="11" fill="#1B5E5A" opacity="0.88" />
          <ellipse cx="21" cy="11" rx="10" ry="11" fill="#C2683E" opacity="0.82" />
          <ellipse cx="17" cy="18" rx="9.5" ry="10" fill="#5B8C5A" opacity="0.82" />
        </svg>

        <div className="auth-eyebrow">{t.eyebrow}</div>
        <h2 id="login-title" className="auth-title">{t.title}</h2>
        <p className="auth-lede">{t.lede}</p>

        <form onSubmit={handleSubmit} className="auth-form">
          {sessionExpired && !error && <div className="auth-notice">{t.expired}</div>}

          <label className="auth-field">
            <span className="auth-label">{t.username}</span>
            <input
              ref={usernameRef}
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoComplete="username"
              required
            />
          </label>

          <label className="auth-field">
            <span className="auth-label">{t.password}</span>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              required
            />
          </label>

          {error && <div className="auth-error">{error}</div>}

          <button type="submit" className="auth-submit" disabled={isLoading}>
            {isLoading ? t.loading : t.submit}
          </button>
        </form>

        <Link to="/reset-password" className="auth-link">{t.forgot}</Link>
      </div>
    </div>
  );
};

export default LoginModal;
